import React, { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageWrapper } from "@/components/PageWrapper";
import { DirectorySkeleton, placeLabel } from "@/components/AgentDirectory";
import AgentDirectorySearch, { useDirectorySearchActions } from "@/components/AgentDirectorySearch";
import BrokerageView from "@/components/BrokerageView";
import DirectoryBreadcrumb, { DIRECTORY_PATH, glue, townPath } from "@/components/DirectoryBreadcrumb";
import DirectoryHome from "@/components/DirectoryHome";
import DirectoryResults from "@/components/DirectoryResults";
import DirectoryStateView from "@/components/DirectoryStateView";
import DirectoryTownView from "@/components/DirectoryTownView";
import { useResolvePlace } from "@/hooks/useAgentDirectory";
import { getLocalStorage } from "@/helpers/common";
import { gitchaAppUrl, useAgentBioLink } from "@/hooks/useAgentBioLink";
import "./AgentDirectoryPage.scss";

export { filtersFromParams } from "@/components/DirectoryResults";

const CARRIED_PARAMS = ["buyers", "q", "brokerage", "network"];

export const locationFromParams = (params) => {
  const latitude = Number(params.get("lat"));
  const longitude = Number(params.get("lng"));
  if (!params.get("lat") || !params.get("lng") || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { city: params.get("city") || "", state: params.get("state") || "", latitude, longitude };
};

export const resolvedPath = (place, params) => {
  const carried = new URLSearchParams();
  CARRIED_PARAMS.forEach((key) => {
    if (params.get(key)) carried.set(key, params.get(key));
  });
  const search = carried.toString();
  return `${townPath(place.stateSlug, place.slug)}${search ? `?${search}` : ""}`;
};

const savedFeedLocation = () => {
  const saved = getLocalStorage("userLocation");
  return saved?.latitude && saved?.longitude
    ? { city: saved.city || "", state: saved.state || "", latitude: Number(saved.latitude), longitude: Number(saved.longitude) }
    : null;
};

const LegacyDirectory = ({ location, params, setParams }) => {
  const navigate = useNavigate();
  const actions = useDirectorySearchActions();
  const resolved = useResolvePlace(location);
  const place = resolved.data?.place;

  useEffect(() => {
    if (place?.stateSlug && place?.slug) navigate(resolvedPath(place, params), { replace: true });
  }, [place, params, navigate]);

  if (resolved.isLoading || place) return <DirectorySkeleton />;

  const label = placeLabel(location);
  const onSearchText = (text) => {
    const next = new URLSearchParams(params);
    next.set("q", text);
    next.delete("brokerage");
    setParams(next, { replace: true });
  };

  return (
    <>
      <Helmet>
        <title>{label ? `Real estate agents in ${label} | Gitcha agent directory` : "Gitcha agent directory"}</title>
      </Helmet>
      <DirectoryBreadcrumb trail={[{ label: "Agents", href: DIRECTORY_PATH }]} />
      <header className="directory-page__head">
        <h1>{label ? glue(`Agents in ${label}`) : "Agent directory"}</h1>
        <p>Agents working with buyers here right now come&nbsp;first.</p>
      </header>
      <AgentDirectorySearch place={label} location={location} onSearchText={onSearchText} {...actions} />
      <DirectoryResults location={location} placeName={location.city} scopeLabel={label} />
    </>
  );
};

export const brokerageNetworkOf = (params) => params.get("n") || params.get("network") || "";

const AgentDirectoryPage = () => {
  const { state, town } = useParams();
  const [params, setParams] = useSearchParams();
  const { pathname, search } = useLocation();
  const { opensGitcha } = useAgentBioLink();
  const gitchaTarget = opensGitcha ? gitchaAppUrl(`${pathname}${search}`) : null;
  const fromUrl = useMemo(() => locationFromParams(params), [params]);
  const brokerageName = params.get("b") || "";

  useEffect(() => {
    if (gitchaTarget) window.location.replace(gitchaTarget);
  }, [gitchaTarget]);

  if (gitchaTarget) {
    return (
      <main className="directory-page">
        <DirectorySkeleton />
      </main>
    );
  }

  const back = () => {
    const next = new URLSearchParams(params);
    next.delete("b");
    next.delete("n");
    next.delete("network");
    setParams(next, { replace: true });
  };

  let body;
  if (town) body = <DirectoryTownView key={`${state}/${town}`} stateSlug={state} townSlug={town} />;
  else if (state) body = <DirectoryStateView key={state} stateSlug={state} location={savedFeedLocation()} />;
  else if (brokerageName) {
    body = (
      <BrokerageView
        name={brokerageName}
        network={brokerageNetworkOf(params)}
        backLabel={fromUrl ? `All agents in ${placeLabel(fromUrl)}` : "Agent directory"}
        onBack={back}
      />
    );
  } else if (fromUrl) body = <LegacyDirectory location={fromUrl} params={params} setParams={setParams} />;
  else body = <DirectoryHome location={savedFeedLocation()} />;

  return (
    <PageWrapper>
      <main className="directory-page">{body}</main>
    </PageWrapper>
  );
};

export default AgentDirectoryPage;
